import React, { useState, useEffect, useCallback, useRef } from 'react';
import BarChart from '../components/BarChart';
import PieChart from '../components/PieChart';
import VerticalBarChart from '../components/VerticalBarChart';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { apiFetch } from '../utils/api';
import { humaniseEndpoint, withOrderNumber } from '../utils/endpointHumaniser';

// 🔻 OWNER RULING 2026-09-17: *"lets keep the last 100 activities of that person
// diplayed"*. Held equal to `ACTIVITY_TIMELINE_DEFAULT` in
// `server/routes/analytics.js` by `regressions/analytics-timeline-limit.test.js`
// — a client asking for LESS than the server default silently wins, so the two
// are one decision written in two trees and must be pinned together.
export const ACTIVITY_TIMELINE_LIMIT = 100;

// ── The OPERATIONAL panels' scope line ───────────────────────────────────────
//
// 🔻 OWNER DECISION 2026-09-18. `/slow-endpoints`, `/error-rate` and `/errors`
// are unfiltered by default and must SAY SO on screen — an unlabelled number is
// how a reader concludes the demo accounts were excluded when they were not.
//
// 🔑 ONE CONTROL, TWO CARDS, SO THE CARD WITHOUT THE CONTROL HAS TO SAY WHERE IT
// IS. The checkbox lives in Error Tracking (two of the three panels are there);
// a reader standing on Slowest Endpoints would otherwise see a state with no
// visible way to change it. Duplicating the checkbox was considered and
// REJECTED: two switches that always move together invite someone to try moving
// one, which is a question the page cannot answer.
export const OPS_SCOPE_ALL_TRAFFIC = 'All traffic (includes demo & test)';
export const OPS_SCOPE_REAL_USERS = 'Real users only';
export const OPS_SCOPE_POINTER = 'change in Error Tracking';
export const OPS_SCOPE_CONTROL_LABEL = 'Exclude demo & test accounts';

/**
 * The scope line for an operational panel.
 * @param {boolean} excluded  is the demo/test exclusion currently on
 * @param {boolean} withPointer  append "— change in Error Tracking" (the card
 *   that does NOT carry the control)
 */
export function opsScopeLabel(excluded, withPointer) {
  const state = excluded ? OPS_SCOPE_REAL_USERS : OPS_SCOPE_ALL_TRAFFIC;
  return withPointer ? `${state} — ${OPS_SCOPE_POINTER}` : state;
}

function formatRelative(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} d ago`;
}

function statusPill(minutesIdle) {
  if (minutesIdle <= 5) return { dot: 'bg-green-500', label: 'Active' };
  if (minutesIdle <= 15) return { dot: 'bg-amber-500', label: 'Idle' };
  return { dot: 'bg-gray-400 dark:bg-gray-500', label: 'Away' };
}

// 🎭 IMPERSONATION MARKER (owner, 2026-09-24): an action a site admin took
// while impersonating must stand out on the Recent users panel. Colour AND
// text — the "via <admin>" words carry it alone for anyone who cannot see the
// tint. Gated on the admin's ID (`impersonated_by`), never the name: a deleted
// admin leaves the id with a NULL name, and that action was still impersonated.
// FUCHSIA because every other hue here already means something — green Active,
// amber Idle (and the app's own impersonation banner, so amber would read as
// "Idle"), gray Away, indigo the ×N session badge, red an error code, blue the
// charts — and `index.css` has no `html.dark` override for any fuchsia class,
// so the `dark:` pair is live. Computed (not browser-measured) text contrast:
// fuchsia-800 on fuchsia-100 7.08:1; fuchsia-200 on fuchsia-900/40 over the
// dark card's #1e293b 9.75:1.
const IMPERSONATION_TAG_CLASS =
  'inline-block whitespace-nowrap text-[11px] font-medium px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200';
const TIMELINE_ENTRY_CLASS = 'flex items-start gap-3 text-xs text-gray-700 dark:text-gray-300';
const IMPERSONATED_ENTRY_TINT ='-mx-1 px-1 rounded bg-fuchsia-50 dark:bg-fuchsia-900/20';

function isImpersonated(entry) {
  return entry.impersonated_by != null;
}

function ImpersonationTag({ entry, className = '', testId }) {
  if (!isImpersonated(entry)) return null;
  const who = entry.impersonated_by_name || null;
  return (
    <span
      className={className ? `${IMPERSONATION_TAG_CLASS} ${className}` : IMPERSONATION_TAG_CLASS}
      title={`Done by ${who || 'a site admin'} while impersonating this user`}
      data-testid={testId}
    >
      via {who || 'an admin'}
    </span>
  );
}

// 📱 THE DEVICE TAG (owner, 2026-10-06): `Mobile` / `Desktop` per timeline
// entry, the device named in the native tooltip. The SERVER decides both
// (`server/helpers/deviceClass.js` — the same rule `/summary` counts with), so
// this renders and never parses. No `device` (no user agent) → no tag. The WORD
// is the carrier; the outline is decoration, which is why it is muted and has
// no fill (a `bg-gray-100` fill is repainted to the dark card's own #1e293b by
// `index.css` and would vanish). Unknown device → tooltip is the class alone.
// iPadOS reports as a Mac, so an iPad reads `Desktop` — accepted (owner).
const DEVICE_TAG_CLASS =
  'inline-block whitespace-nowrap text-[11px] font-medium px-1.5 py-0.5 rounded border border-gray-300 text-gray-500 dark:border-gray-500 dark:text-gray-400';

export function DeviceTag({ entry, testId }) {
  if (entry.device !== 'Mobile' && entry.device !== 'Desktop') return null;
  return (
    <span className={DEVICE_TAG_CLASS} title={entry.device_detail || entry.device} data-testid={testId}>
      {entry.device}
    </span>
  );
}

// ── TOP 10 SCREEN SIZES (owner, 2026-10-06) ─────────────────────────────────
// Sizes are only recorded from the release that added them, so the card is
// EMPTY until data accrues and says so rather than showing a blank chart.
export const SCREEN_SIZES_RECORDED_FROM = 'the October 2026 release';
export const SCREEN_SIZES_CAPTION =
  'Last 12 months. Each person counted once per device type, at their latest size.';
const SCREEN_SIZE_CLASSES = [
  { key: 'mobile', label: 'Mobile' },
  { key: 'desktop', label: 'Desktop' },
];

function isScreenSizeClass(c) {
  return !!c && typeof c === 'object' && Array.isArray(c.sizes) && Number.isFinite(c.people);
}

export function ScreenSizesCard({ data }) {
  const [deviceClass, setDeviceClass] = useState('mobile');
  const valid = !!data && isScreenSizeClass(data.mobile) && isScreenSizeClass(data.desktop);
  const nothingYet = !valid || (data.mobile.people === 0 && data.desktop.people === 0);
  const current = valid ? data[deviceClass] : null;
  const top = current && current.sizes.length > 0 ? Math.max(...current.sizes.map((s) => s.percent)) : 0;
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 mt-6" data-testid="analytics-screen-sizes">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Top 10 screen sizes</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{SCREEN_SIZES_CAPTION}</p>
        </div>
        {!nothingYet && (
          <div role="group" aria-label="Device type" className="inline-flex rounded-full bg-gray-100 dark:bg-gray-700 p-0.5" data-testid="analytics-screen-sizes-toggle">
            {SCREEN_SIZE_CLASSES.map((c) => (
              <button
                key={c.key}
                type="button"
                aria-pressed={deviceClass === c.key}
                onClick={() => setDeviceClass(c.key)}
                className={`min-h-[44px] md:min-h-0 px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  deviceClass === c.key
                    ? 'bg-blue-600 dark:bg-blue-500/70 text-white'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
                }`}
                data-testid={`analytics-screen-sizes-${c.key}`}
              >
                {c.label} ({data[c.key].people})
              </button>
            ))}
          </div>
        )}
      </div>
      {nothingYet ? (
        <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8" data-testid="analytics-screen-sizes-empty">
          No screen sizes recorded yet. Sizes are recorded from {SCREEN_SIZES_RECORDED_FROM} onward.
        </p>
      ) : current.sizes.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8" data-testid="analytics-screen-sizes-class-empty">
          No {deviceClass} visits with a recorded size yet.
        </p>
      ) : (
        <ol className="space-y-2" data-testid="analytics-screen-sizes-list">
          {current.sizes.map((s, i) => (
            <li key={`${s.width}x${s.height}`} className="grid grid-cols-[6.5rem_1fr_auto] items-center gap-3 text-sm" data-testid={`analytics-screen-size-${i}`}>
              <span className="tabular-nums text-gray-800 dark:text-gray-200">{s.width} × {s.height}</span>
              <span className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden" aria-hidden="true">
                <span className="block h-full rounded-full bg-blue-500 dark:bg-blue-400" style={{ width: `${top > 0 ? (s.percent / top) * 100 : 0}%` }} />
              </span>
              <span className="tabular-nums text-right whitespace-nowrap text-gray-700 dark:text-gray-300">
                {s.percent}%
                <span className="ml-1.5 text-xs text-gray-500 dark:text-gray-400">{s.people} {s.people === 1 ? 'person' : 'people'}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function shortUserAgent(ua) {
  if (!ua) return '';
  if (/iPhone|iPad/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/Chrome\//i.test(ua)) return 'Chrome';
  if (/Safari\//i.test(ua)) return 'Safari';
  if (/Firefox\//i.test(ua)) return 'Firefox';
  return 'Browser';
}

const PAGE_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'
];

const HOUR_COLORS = [
  '#1E3A5F', '#1E3A5F', '#1E3A5F', '#1E3A5F', '#1E3A5F', '#2563EB',
  '#3B82F6', '#60A5FA', '#93C5FD', '#60A5FA', '#3B82F6', '#2563EB',
  '#3B82F6', '#60A5FA', '#93C5FD', '#60A5FA', '#3B82F6', '#2563EB',
  '#1E40AF', '#1E3A5F', '#1E3A5F', '#1E3A5F', '#1E3A5F', '#1E3A5F'
];

// ── THE PERIOD TOGGLE — WEEK / MONTH / YEAR, AND THERE IS NO "ALL" ──────────
//
// 🔻 OWNER RULING 2026-09-21: *"Why not a page toggle for weekly stats, monthly
// stats and year. So the page by default loads 1 week of stats then I have the
// option to select month or year. This should help with keeping the number of
// requests down? Also cap at the year 365 days."*
//
// 🔻 ONE CORRECTION TO THAT FRAMING, RECORDED SO IT IS NOT RE-DERIVED AS A
// SAVING THAT DID NOT HAPPEN: this does NOT reduce the NUMBER of requests. The
// page still fires nine reads in one `Promise.all` below, exactly as before.
// What it reduces is what each one READS. Measured on staging 2026-09-20 over
// 955,683 `activity_log` rows, with the toggle on "All": `/summary` averaged
// **6,577 ms** and `/active-users` **5,061 ms**, because every one of them
// aggregated the whole table.
//
// 🚨 `'all'` IS GONE AND MUST NOT COME BACK. It was not a wide setting that
// needed tuning; it was the single reason eight endpoints were unbounded. The
// server now refuses to build an unbounded clause at all — see `periodFilter`
// in `server/routes/analytics.js` and the ZERO pinned by
// `server/tests/regressions/analytics-unbounded-scan-manifest.test.js`.
const PERIOD_OPTIONS = [
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

const DEFAULT_PERIOD = 'week';

// 🚨 THE CARD SUBTITLES ARE DERIVED, NEVER TYPED, AND THAT IS THE POINT.
// Both summary cards used to carry a hard-coded claim about their own scope,
// and the two were WRONG IN DIFFERENT WAYS — worth separating, because only one
// of them is the failure mode this repo already has a name for:
//
//   • *"All time"* on Total Requests — **DRIFTED**. True while an unbounded
//     option existed, falsified the moment it stopped existing. This repo's
//     recorded shape: *a cadence change can falsify copy that nobody edited*.
//   • *"Last 30 days"* on Active Users — **NEVER TRUE, NOT DRIFTED.** It was
//     wrong the day it was written. `/summary` returns `COUNT(DISTINCT
//     username)` over whatever `?period` says, and `?period` defaulted to ALL
//     of history — so this card showed an ALL-TIME figure under a 30-DAY label,
//     on the owner's own analytics page, for as long as it existed.
//     🕶️ NOTHING COULD HAVE CAUGHT IT. No test in this repo compares a subtitle
//     to the SQL underneath it, and a label that never matched its query has no
//     "before" state a regression test could pin. It was found by reading the
//     two lines next to each other, which is the only instrument there is.
//
// 🔑 THE DURABLE HALF OF THE FIX IS THAT THESE ARE NOW DERIVED. A subtitle
// computed from the same `period` that builds the query string cannot rot the
// way a typed one did: change the window and the label follows, because there
// is only one fact and both readers share it. **Do not "simplify" this back to
// a literal**, however obvious the current wording looks — a literal here is
// how both of the above happened.
//
// 📏 THE WORDING IS THE WINDOW, not a friendly paraphrase. "This week" would be
// wrong — the server's `week` is a rolling `now - 7 days`, not the calendar
// week — and `year` is exactly 365 days by the owner's cap, so it says so.
const PERIOD_SUBTITLE = {
  week: 'Last 7 days',
  month: 'Last month',
  year: 'Last 365 days',
};

function periodSubtitle(p) {
  return PERIOD_SUBTITLE[p] || PERIOD_SUBTITLE[DEFAULT_PERIOD];
}

const LS_KEY_PERIOD = 'sfx_analytics_period';

// 🚨 THE STORED VALUE IS SANITISED ON EVERY READ, AND CHANGING THE DEFAULT
// ALONE WOULD HAVE REACHED NOBODY. This key has been written since the toggle
// shipped, so **everyone who has ever opened this page has `'all'` in
// `localStorage`** — and `getInitialPeriod` used to hand that straight back.
// A new default would therefore have applied only to people who had never used
// the feature, which is the opposite of who the change is for. Every value the
// toggle no longer offers (`'all'`, `'today'`, anything a hand-edited storage
// entry contains) resolves to Week here.
//
// 🪤 RESOLVED ON READ, **NOT MIGRATED ON MOUNT**, and that is the directory
// rule rather than a preference — `client/src/hooks/CLAUDE.md`: *"A HOOK THAT
// PERSISTS MUST NOT WRITE ON HYDRATION"*. Rewriting the stale key here would
// stamp a value into storage for somebody who never touched the control, after
// which a later change to the default is silently overridden for them by a
// choice they never made. The stale `'all'` simply stays in storage, unread and
// harmless, until the user clicks a button. **Do not add a `setItem` to this
// function or to a mount effect.**
function getInitialPeriod() {
  let stored = null;
  try {
    stored = localStorage.getItem(LS_KEY_PERIOD);
  } catch {
    stored = null;
  }
  return PERIOD_OPTIONS.some(o => o.key === stored) ? stored : DEFAULT_PERIOD;
}

function AdminAnalytics() {
  const [period, setPeriod] = useState(getInitialPeriod);
  const [summary, setSummary] = useState(null);
  const [pagePopularity, setPagePopularity] = useState([]);
  const [usageOverTime, setUsageOverTime] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [featureAdoption, setFeatureAdoption] = useState([]);
  const [slowEndpoints, setSlowEndpoints] = useState([]);
  const [errors289, setErrors289] = useState([]);
  const [errorRate, setErrorRate] = useState([]);
  // ── The OPERATIONAL panels' scope — ONE piece of state for all THREE ────────
  //
  // 🔻 OWNER DECISION 2026-09-18. Every USAGE read on this page is restricted to
  // real (non-demo, non-test) accounts server-side and takes no parameter. The
  // three OPERATIONAL reads — `/slow-endpoints`, `/error-rate`, `/errors` — are
  // deliberately NOT: filtering them by default could hide real breakage, since
  // a 500 is a 500 whoever hit it and the demo logins are what a pilot tester is
  // driving when something falls over. So they stay unfiltered, are LABELLED as
  // all traffic, and this is the admin's opt-in.
  //
  // 🔑 ONE TOGGLE, THREE PANELS, AND THAT IS A CORRECTNESS DECISION RATHER THAN
  // A PREFERENCE. `/error-rate` and `/errors` are a CHART AND THE ROWS BEHIND
  // IT, rendered in one card: let them disagree about their population and the
  // page shows an error rate over one set of traffic and the failing requests
  // from another, with nothing on screen to explain the mismatch. They are also
  // all fetched in ONE `Promise.all` off one `period`, so per-panel scope would
  // mean either splitting that fetch or refetching all nine to change one card.
  const [excludeDemoOps, setExcludeDemoOps] = useState(false);
  const [userSortField, setUserSortField] = useState('request_count');
  const [userSortAsc, setUserSortAsc] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Currently Active panel — populated by polling /api/analytics/active-now
  // every 30s while the tab is visible. Expanded rows lazy-fetch the
  // per-user timeline.
  const [activeNow, setActiveNow] = useState([]);
  const [activeNowFetchedAt, setActiveNowFetchedAt] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [recentActivityById, setRecentActivityById] = useState({});
  const [recentActivityLoading, setRecentActivityLoading] = useState(false);
  const fetchActiveNowRef = useRef(null);
  // Top 10 screen sizes — its own fixed 12-month window, so fetched ONCE,
  // independent of the period toggle and of the nine-read Promise.all.
  const [screenSizes, setScreenSizes] = useState(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await apiFetch('/api/analytics/screen-sizes');
        if (res.ok && live) setScreenSizes(await res.json());
      } catch {
        // The card shows its empty state; nothing else depends on it.
      }
    })();
    return () => { live = false; };
  }, []);

  // 🔑 `excludeOps` IS AN ARGUMENT, NOT A CLOSURE READ. `fetchData` is a
  // `useCallback` with an empty dependency array, so reading the state here
  // would capture the value from first render and the toggle would appear to do
  // nothing until some unrelated re-render happened to refresh the callback.
  // The effect below passes both inputs explicitly.
  const fetchData = useCallback(async (p, excludeOps) => {
    setLoading(true);
    setError(null);
    try {
      // 🔑 ALWAYS SENT, NEVER EMPTY. This used to omit the parameter for
      // `'all'`, which is precisely how eight endpoints ended up scanning the
      // whole `activity_log`. `p` is sanitised by `getInitialPeriod` and can
      // only ever be one of `PERIOD_OPTIONS`; the server falls closed on
      // anything else regardless, so neither side depends on the other.
      const qs = `?period=${encodeURIComponent(p || DEFAULT_PERIOD)}`;
      // 🔑 ONLY THE THREE OPERATIONAL READS EVER CARRY `exclude_demo`. The six
      // usage reads are restricted server-side with no opt-out; sending it to
      // them would be a control that reads as meaningful and is ignored.
      const opsQs = excludeOps ? `${qs}&exclude_demo=1` : qs;
      const [summaryRes, popularityRes, usageRes, usersRes, peakRes, adoptionRes, slowRes, errorsRes, errorRateRes] = await Promise.all([
        apiFetch(`/api/analytics/summary${qs}`),
        apiFetch(`/api/analytics/page-popularity${qs}`),
        apiFetch(`/api/analytics/usage-over-time${qs}`),
        apiFetch(`/api/analytics/active-users${qs}`),
        apiFetch(`/api/analytics/peak-hours${qs}`),
        apiFetch(`/api/analytics/feature-adoption${qs}`),
        apiFetch(`/api/analytics/slow-endpoints${opsQs}`),
        apiFetch(`/api/analytics/errors${opsQs}`),
        apiFetch(`/api/analytics/error-rate${opsQs}`),
      ]);

      if (!summaryRes.ok) throw new Error('Failed to load summary');
      if (!popularityRes.ok) throw new Error('Failed to load page popularity');

      const summaryData = await summaryRes.json();
      const popularityData = await popularityRes.json();
      const usageData = usageRes.ok ? await usageRes.json() : [];
      const usersData = usersRes.ok ? await usersRes.json() : [];
      const peakData = peakRes.ok ? await peakRes.json() : [];
      const adoptionData = adoptionRes.ok ? await adoptionRes.json() : [];
      const slowData = slowRes.ok ? await slowRes.json() : [];
      const errorsData = errorsRes.ok ? await errorsRes.json() : [];
      const errorRateData = errorRateRes.ok ? await errorRateRes.json() : [];

      setSummary(summaryData);
      setPagePopularity(popularityData);
      setUsageOverTime(usageData);
      setActiveUsers(usersData);
      setPeakHours(peakData);
      setFeatureAdoption(adoptionData);
      setSlowEndpoints(slowData);
      setErrors289(errorsData);
      setErrorRate(errorRateData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(period, excludeDemoOps);
  }, [period, excludeDemoOps, fetchData]);

  // Currently Active panel — independent of the period selector. Polls every
  // 30s while the page is visible; pauses when the tab is hidden to keep the
  // request volume low.
  const fetchActiveNow = useCallback(async () => {
    try {
      const res = await apiFetch('/api/analytics/active-now');
      if (!res.ok) return; // silently skip — error UI handled by the main fetch
      const rows = await res.json();
      setActiveNow(Array.isArray(rows) ? rows : []);
      setActiveNowFetchedAt(new Date());
    } catch {
      // Network blip — keep last good list, try again on next tick.
    }
  }, []);
  fetchActiveNowRef.current = fetchActiveNow;

  useEffect(() => {
    fetchActiveNow();
    let timer = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => fetchActiveNowRef.current?.(), 30_000);
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchActiveNowRef.current?.();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchActiveNow]);

  const handleExpandActiveRow = useCallback(async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    if (recentActivityById[userId]) return; // already cached
    setRecentActivityLoading(true);
    try {
      // 🔻 100, OWNER RULING 2026-09-17: *"lets keep the last 100 activities of
      // that person diplayed"*. 🪤 THE SCREEN SHOWS `min(this ask, the server's
      // cap)`, so raising the server default alone would have changed NOTHING
      // while this line still said 20 — which is exactly how it came to show 20
      // against a server default of 30. Both ends are pinned by
      // `server/tests/regressions/analytics-timeline-limit.test.js`.
      const res = await apiFetch(`/api/analytics/users/${userId}/recent-activity?limit=${ACTIVITY_TIMELINE_LIMIT}`);
      if (res.ok) {
        const rows = await res.json();
        setRecentActivityById(prev => ({ ...prev, [userId]: rows }));
      }
    } catch {
      // ignore — the timeline just won't load; close-and-retry will re-fetch.
    } finally {
      setRecentActivityLoading(false);
    }
  }, [expandedUserId, recentActivityById]);

  const handlePeriodChange = (key) => {
    setPeriod(key);
    try {
      localStorage.setItem(LS_KEY_PERIOD, key);
    } catch {}
  };

  // Compute mobile percentage
  const mobilePercent = summary && summary.total_requests > 0
    ? Math.round((summary.mobile_requests / summary.total_requests) * 100)
    : 0;

  // Transform page popularity for BarChart
  const chartData = pagePopularity.map((item, i) => ({
    label: item.page,
    value: item.requests,
    color: PAGE_COLORS[i % PAGE_COLORS.length],
  }));

  if (loading && !summary) {
    return (
      <div className="p-2.5 max-w-full">
        <div className="mt-6"><LoadingSpinner /></div>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="p-2.5 max-w-full">
        <div className="mt-6"><ErrorMessage error={error} /></div>
      </div>
    );
  }

  return (
    <div className="p-2.5 max-w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-1" data-testid="analytics-period-toggle">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.key}
              onClick={() => handlePeriodChange(opt.key)}
              className={`min-h-[44px] md:min-h-0 inline-flex items-center px-4 md:px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                period === opt.key
                  ? 'bg-blue-600 dark:bg-blue-500/70 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
              data-testid={`analytics-period-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error && <ErrorMessage error={error} />}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="analytics-summary-cards">
        <SummaryCard
          title="Active Users"
          value={summary?.total_users ?? 0}
          subtitle={periodSubtitle(period)}
          icon={
            <svg className="h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
            </svg>
          }
        />
        <SummaryCard
          title="Total Requests"
          value={summary?.total_requests?.toLocaleString() ?? '0'}
          subtitle={periodSubtitle(period)}
          icon={
            <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
            </svg>
          }
        />
        <SummaryCard
          title="Avg Response"
          value={`${summary?.avg_response_time_ms ?? 0} ms`}
          subtitle="Response time"
          icon={
            <svg className="h-8 w-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <SummaryCard
          title="Mobile vs Desktop"
          value={`${mobilePercent}%`}
          subtitle={`${summary?.mobile_requests ?? 0} mobile / ${summary?.desktop_requests ?? 0} desktop`}
          icon={
            <svg className="h-8 w-8 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
            </svg>
          }
        />
      </div>

      {/* Page Popularity Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6" data-testid="analytics-page-popularity">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Page Popularity</h3>
        </div>
        {chartData.length > 0 ? (
          <BarChart
            data={chartData}
            maxBars={12}
          />
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">No page visit data available for this period.</p>
        )}
      </div>

      {/* Usage Over Time Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 mt-6" data-testid="analytics-usage-over-time">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Usage Over Time</h3>
        </div>
        {usageOverTime.length > 0 ? (
          <div>
            <VerticalBarChart
              data={usageOverTime.map(item => ({
                label: item.period,
                value: item.requests,
              }))}
              color="#3B82F6"
              height={280}
            />
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-1.5 px-3 font-medium text-gray-600 dark:text-gray-400">Period</th>
                    <th className="text-right py-1.5 px-3 font-medium text-gray-600 dark:text-gray-400">Requests</th>
                    <th className="hidden md:table-cell text-right py-1.5 px-3 font-medium text-gray-600 dark:text-gray-400">Unique Users</th>
                  </tr>
                </thead>
                <tbody>
                  {usageOverTime.slice(-10).map((item, i) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
                      <td className="py-1.5 px-3 text-gray-900 dark:text-gray-100">{item.period}</td>
                      <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">{item.requests.toLocaleString()}</td>
                      <td className="hidden md:table-cell py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">{item.unique_users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">No usage data available for this period.</p>
        )}
      </div>

      {/* Recent Users — last 100 real users to use the site, ordered by
          most recent activity. Polls every 30s for freshness. */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 mt-6" data-testid="analytics-active-now">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Recent users</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Last 100 people to use the site, newest first. Demo accounts excluded.
              {activeNowFetchedAt && (
                <span className="ml-2">Updated {formatRelative(activeNowFetchedAt.toISOString())}.</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchActiveNow()}
            className="text-xs px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
            data-testid="analytics-active-now-refresh"
          >
            Refresh
          </button>
        </div>
        {activeNow.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">
            No recent activity yet.
          </p>
        ) : (
          // Sized for exactly 10 body rows plus the sticky header; rows
          // 11+ scroll. Each row renders the user's name + email stacked,
          // so ~52px per row → 10 × 52 + 32 (header) ≈ 552px.
          <div className="overflow-auto max-h-[552px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white dark:bg-gray-800 z-10">
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-1.5 px-3 font-medium text-gray-600 dark:text-gray-400">Status</th>
                  <th className="text-left py-1.5 px-3 font-medium text-gray-600 dark:text-gray-400">User</th>
                  <th className="hidden md:table-cell text-left py-1.5 px-3 font-medium text-gray-600 dark:text-gray-400">Role</th>
                  <th className="text-left py-1.5 px-3 font-medium text-gray-600 dark:text-gray-400">Last seen</th>
                  <th className="text-left py-1.5 px-3 font-medium text-gray-600 dark:text-gray-400">Last page</th>
                  <th className="hidden lg:table-cell text-left py-1.5 px-3 font-medium text-gray-600 dark:text-gray-400">From</th>
                </tr>
              </thead>
              <tbody>
                {activeNow.map((row) => {
                  const pill = statusPill(row.minutes_idle);
                  // null = endpoint is noise (background poll or unmapped GET);
                  // show a dash so the table doesn't go ragged.
                  const page = withOrderNumber(humaniseEndpoint(row.last_method, row.last_endpoint), row.order_ref) || '—';
                  const isExpanded = expandedUserId === row.user_id;
                  const timeline = recentActivityById[row.user_id];
                  return (
                    <React.Fragment key={row.user_id}>
                      <tr
                        onClick={() => handleExpandActiveRow(row.user_id)}
                        className={`border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-colors ${
                          isExpanded
                            ? 'bg-gray-100 dark:bg-slate-700/60'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                        }`}
                        data-testid={`analytics-active-row-${row.user_id}`}
                      >
                        <td className="py-1.5 px-3">
                          <span className="inline-flex items-center gap-2">
                            <span className={`inline-block h-2 w-2 rounded-full ${pill.dot}`} aria-hidden="true" />
                            <span className="text-xs text-gray-600 dark:text-gray-300">{pill.label}</span>
                          </span>
                        </td>
                        <td className="py-1.5 px-3 text-gray-900 dark:text-gray-100">
                          <div className="font-medium">{row.display_name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{row.email}</div>
                        </td>
                        <td className="hidden md:table-cell py-1.5 px-3 text-gray-700 dark:text-gray-300 capitalize">{row.system_role || 'user'}</td>
                        <td className="py-1.5 px-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {formatRelative(row.last_activity_at)}
                          {row.session_count > 1 && (
                            <span className="ml-2 inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                              ×{row.session_count}
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 px-3 text-gray-700 dark:text-gray-300">
                          {page}
                          <ImpersonationTag entry={row} className="ml-2" testId={`analytics-active-row-${row.user_id}-impersonated`} />
                        </td>
                        <td className="hidden lg:table-cell py-1.5 px-3 text-xs text-gray-500 dark:text-gray-400">
                          {shortUserAgent(row.user_agent)}{row.ip_address ? ` · ${row.ip_address}` : ''}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-600">
                          <td colSpan={6} className="py-3 px-4">
                            <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Recent actions</div>
                            {recentActivityLoading && !timeline && <LoadingSpinner />}
                            {timeline && timeline.length === 0 && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">No activity recorded yet.</p>
                            )}
                            {timeline && timeline.length > 0 && (() => {
                              // Drop rows the humaniser can't translate — the feed
                              // should never show raw "GET /api/x/y" strings.
                              const visible = timeline
                                .map((entry) => ({
                                  entry,
                                  label: withOrderNumber(humaniseEndpoint(entry.method, entry.endpoint), entry.order_ref),
                                }))
                                .filter((row) => row.label);
                              if (visible.length === 0) {
                                return (
                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                    No meaningful actions recorded yet.
                                  </p>
                                );
                              }
                              return (
                                <ol className="space-y-1.5">
                                  {visible.map(({ entry, label }, i) => (
                                    <li
                                      key={i}
                                      className={isImpersonated(entry) ? `${TIMELINE_ENTRY_CLASS} ${IMPERSONATED_ENTRY_TINT}` : TIMELINE_ENTRY_CLASS}
                                      data-testid={`analytics-timeline-entry-${i}`}
                                    >
                                      <span className="text-gray-400 dark:text-gray-500 whitespace-nowrap">{formatRelative(entry.timestamp)}</span>
                                      <span className="flex-1">{label}</span>
                                      <DeviceTag entry={entry} testId={`analytics-timeline-entry-${i}-device`} />
                                      <ImpersonationTag entry={entry} testId={`analytics-timeline-entry-${i}-impersonated`} />
                                      {entry.status_code >= 400 && (
                                        <span className="text-red-600 dark:text-red-400 font-medium">{entry.status_code}</span>
                                      )}
                                    </li>
                                  ))}
                                </ol>
                              );
                            })()}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ScreenSizesCard data={screenSizes} />

      {/* Most Active Users Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 mt-6" data-testid="analytics-active-users">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Most Active Users</h3>
        </div>
        {activeUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  {[
                    { key: 'username', label: 'Username', mobile: true },
                    { key: 'request_count', label: 'Requests', mobile: true },
                    { key: 'last_active', label: 'Last Active', mobile: false },
                    { key: 'top_feature', label: 'Top Feature', mobile: false },
                  ].map(col => (
                    <th
                      key={col.key}
                      className={`${col.mobile ? '' : 'hidden md:table-cell'} text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200 select-none`}
                      onClick={() => {
                        if (userSortField === col.key) {
                          setUserSortAsc(!userSortAsc);
                        } else {
                          setUserSortField(col.key);
                          setUserSortAsc(col.key === 'username');
                        }
                      }}
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {userSortField === col.key && (
                          <span className="text-blue-500">{userSortAsc ? '\u2191' : '\u2193'}</span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...activeUsers].sort((a, b) => {
                  const aVal = a[userSortField];
                  const bVal = b[userSortField];
                  if (typeof aVal === 'number') return userSortAsc ? aVal - bVal : bVal - aVal;
                  const aStr = (aVal || '').toLowerCase();
                  const bStr = (bVal || '').toLowerCase();
                  return userSortAsc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
                }).map((user, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="py-2 px-3 text-gray-900 dark:text-gray-100 font-medium">{user.username}</td>
                    <td className="py-2 px-3 text-gray-700 dark:text-gray-300">{user.request_count.toLocaleString()}</td>
                    <td className="hidden md:table-cell py-2 px-3 text-gray-500 dark:text-gray-400">
                      {user.last_active ? new Date(user.last_active).toLocaleString() : '-'}
                    </td>
                    <td className="hidden md:table-cell py-2 px-3">
                      <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                        {user.top_feature}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">No active user data available for this period.</p>
        )}
      </div>

      {/* Peak Usage Hours Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 mt-6 mb-6" data-testid="analytics-peak-hours">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Peak Usage Hours</h3>
        </div>
        {peakHours.length > 0 ? (
          <VerticalBarChart
            data={peakHours.map(item => ({
              label: `${String(item.hour).padStart(2, '0')}:00`,
              value: item.requests,
            }))}
            color="#10B981"
            height={280}
          />
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">No peak hours data available for this period.</p>
        )}
      </div>

      {/* Feature Adoption Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 mt-6" data-testid="analytics-feature-adoption">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Feature Adoption</h3>
        </div>
        {featureAdoption.length > 0 ? (
          <div className="flex justify-center">
            <PieChart
              data={featureAdoption.map((item, i) => ({
                label: item.feature_area,
                value: item.requests,
                color: PAGE_COLORS[i % PAGE_COLORS.length],
              }))}
              size={280}
            />
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">No feature adoption data available for this period.</p>
        )}
      </div>

      {/* Slowest Endpoints Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 mt-6" data-testid="analytics-slow-endpoints">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Slowest Endpoints</h3>
          {/* Scope line only — the control lives in Error Tracking, and this
              names where, so the state is never unchangeable from here. */}
          <span
            className="text-xs text-gray-500 dark:text-gray-400"
            data-testid="analytics-slow-endpoints-scope"
          >
            {opsScopeLabel(excludeDemoOps, true)}
          </span>
        </div>
        {slowEndpoints.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Endpoint</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Avg ms</th>
                  <th className="hidden md:table-cell text-right py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Requests</th>
                </tr>
              </thead>
              <tbody>
                {slowEndpoints.map((ep, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750">
                    <td className="py-2 px-3 text-gray-800 dark:text-gray-200 font-mono text-xs max-w-[200px] md:max-w-none truncate" title={ep.endpoint}>{ep.endpoint}</td>
                    <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">
                      <span className={ep.avg_response_time_ms > 500 ? 'text-red-500 font-semibold' : ep.avg_response_time_ms > 200 ? 'text-yellow-500 font-semibold' : ''}>
                        {ep.avg_response_time_ms}
                      </span>
                    </td>
                    <td className="hidden md:table-cell py-2 px-3 text-right text-gray-700 dark:text-gray-300">{ep.request_count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">No endpoint performance data available for this period.</p>
        )}
      </div>

      {/* Error Tracking Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 mt-6 mb-6" data-testid="analytics-error-tracking">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Error Tracking</h3>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className="text-xs text-gray-500 dark:text-gray-400"
              data-testid="analytics-error-tracking-scope"
            >
              {opsScopeLabel(excludeDemoOps, false)}
            </span>
            {/* THE one control for all three operational panels. Flipping it
                re-queries; it never touches the six usage panels. */}
            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                checked={excludeDemoOps}
                onChange={(e) => setExcludeDemoOps(e.target.checked)}
                data-testid="analytics-exclude-demo-toggle"
              />
              {OPS_SCOPE_CONTROL_LABEL}
            </label>
          </div>
        </div>

        {/* Error Rate Chart */}
        {errorRate.length > 0 ? (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Error Rate Over Time</h4>
            <VerticalBarChart
              data={errorRate.map(item => ({
                label: item.period,
                value: item.error_count,
                fullLabel: `${item.period}: ${item.error_count} errors (${item.error_rate}%)`,
              }))}
              color="#EF4444"
              height={220}
            />
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4 mb-4">No error rate data available for this period.</p>
        )}

        {/* Recent Errors Table */}
        <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Recent Failed Requests</h4>
        {errors289.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="hidden md:table-cell text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Timestamp</th>
                  <th className="hidden md:table-cell text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">User</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Endpoint</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {errors289.slice(0, 50).map((errItem, i) => {
                  const is5xx = errItem.status_code >= 500;
                  const is4xx = errItem.status_code >= 400 && errItem.status_code < 500;
                  return (
                    <tr key={i} className={`border-b border-gray-100 dark:border-gray-700 ${is5xx ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                      <td className="hidden md:table-cell py-2 px-3 text-gray-600 dark:text-gray-400 text-xs whitespace-nowrap">
                        {new Date(errItem.timestamp).toLocaleString()}
                      </td>
                      <td className="hidden md:table-cell py-2 px-3 text-gray-800 dark:text-gray-200 text-xs">{errItem.username || 'Anonymous'}</td>
                      <td className="py-2 px-3 text-gray-700 dark:text-gray-300 font-mono text-xs max-w-[200px] md:max-w-none truncate" title={`${errItem.method} ${errItem.endpoint}`}>{errItem.method} {errItem.endpoint}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                          is5xx ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' :
                          is4xx ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' :
                          'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {errItem.status_code}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No errors recorded for this period.</p>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ title, value, subtitle, icon }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-start gap-3" data-testid={`summary-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex-shrink-0 mt-1">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</p>
        <p className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-1">{value}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
}

export default AdminAnalytics;
